import { getAtuacao } from '../delegacao/atuacao';
import { get, set, del, keys } from 'idb-keyval';
import { hasSpaceFor } from './storageManager';
import { evictIfNeeded } from './evictionManager';
import { detectFailureReport } from './failureReport';
import { getDocsCacheName, getDocMetaPrefix, getUserIdOrNull } from './userScope';

export interface DocumentMeta {
  id: string;
  processoId?: string;
  size: number;
  cachedAt: string;
  lastAccessedAt: string;
  mimeType: string;
  fileName?: string;
}

function stableKey(id: string): string {
  return '/docs/' + id;
}

function metaKey(id: string): string {
  return getDocMetaPrefix() + id;
}

export async function cacheDocument(
  id: string,
  presignedUrl: string,
  meta?: Partial<DocumentMeta>,
): Promise<boolean> {
  if (!getUserIdOrNull()) {
    throw new Error('Não é possível cachear documento sem usuário autenticado');
  }
  // Documento da conta do titular não fica no disco do delegado (P11).
  if (getAtuacao()) return false;
  try {
    const response = await fetch(presignedUrl);
    if (!response.ok) return false;

    const blob = await response.blob();
    // Página/frase de erro entregue com 200 no lugar da peça: guardar seria
    // pior que não guardar, porque o cache é servido antes da rede.
    if (await detectFailureReport(blob)) return false;
    const size = blob.size;

    if (!await hasSpaceFor(size)) {
      // Tentar liberar espaço via eviction LRU antes de desistir
      await evictIfNeeded();
      if (!await hasSpaceFor(size)) return false;
    }

    const cache = await caches.open(getDocsCacheName());
    const cacheResponse = new Response(blob, {
      headers: {
        'Content-Type': meta?.mimeType ?? blob.type ?? 'application/octet-stream',
      },
    });
    await cache.put(new Request(stableKey(id)), cacheResponse);

    const now = new Date().toISOString();
    const docMeta: DocumentMeta = {
      id,
      processoId: meta?.processoId,
      size,
      cachedAt: now,
      lastAccessedAt: now,
      mimeType: meta?.mimeType ?? blob.type ?? 'application/octet-stream',
      fileName: meta?.fileName,
    };
    await set(metaKey(id), docMeta);

    return true;
  } catch {
    return false;
  }
}

/**
 * Auto-cura: apaga a entrada quando o que está guardado é um relato de falha
 * (gravado antes desta guarda existir). Devolve `true` se apagou.
 */
async function purgeIfFailureReport(id: string, cached: Response): Promise<boolean> {
  if (!(await detectFailureReport(await cached.clone().blob()))) return false;
  await removeDocument(id);
  return true;
}

export async function getCachedDocument(id: string): Promise<Response | null> {
  if (!getUserIdOrNull()) return null;
  try {
    const cache = await caches.open(getDocsCacheName());
    const response = await cache.match(new Request(stableKey(id)));
    if (!response) return null;
    if (await purgeIfFailureReport(id, response)) return null;

    // Update lastAccessedAt
    try {
      const existing = await get<DocumentMeta>(metaKey(id));
      if (existing) {
        existing.lastAccessedAt = new Date().toISOString();
        await set(metaKey(id), existing);
      }
    } catch {
      // Non-critical — don't fail the read
    }

    return response;
  } catch {
    return null;
  }
}

export async function isDocumentCached(id: string): Promise<boolean> {
  if (!getUserIdOrNull()) return false;
  try {
    const cache = await caches.open(getDocsCacheName());
    const match = await cache.match(new Request(stableKey(id)));
    if (!match) return false;
    // Mesma guarda da leitura: quem pergunta "já tenho?" decide se baixa.
    // Responder `true` para um relato faria o caller pular o download e depois
    // achar `null` na leitura.
    return !(await purgeIfFailureReport(id, match));
  } catch {
    return false;
  }
}

export async function removeDocument(id: string): Promise<void> {
  if (!getUserIdOrNull()) return;
  try {
    const cache = await caches.open(getDocsCacheName());
    await cache.delete(new Request(stableKey(id)));
  } catch {
    // Degrade silently
  }

  try {
    await del(metaKey(id));
  } catch {
    // Degrade silently
  }
}

/**
 * Alias semântico de `removeDocument` voltado ao caso "limpar blob corrompido
 * antes de retentar o download". Tem a mesma assinatura e efeito — apaga o
 * blob no Cache Storage e o meta no IndexedDB — mas comunica a intenção do
 * caller (auto-cura no DocumentViewer) e mantém um ponto de instrumentação
 * caso queiramos auditar especificamente essa via no futuro (ex.: contador
 * de "cache invalidado por erro de render").
 */
export async function clearCachedDocument(id: string): Promise<void> {
  return removeDocument(id);
}

export async function getCachedDocumentMetas(): Promise<DocumentMeta[]> {
  if (!getUserIdOrNull()) return [];
  try {
    const prefix = getDocMetaPrefix();
    const allKeys = await keys<string>();
    const metaKeys = allKeys.filter(
      (k) => typeof k === 'string' && k.startsWith(prefix),
    );

    const metas: DocumentMeta[] = [];
    for (const key of metaKeys) {
      try {
        const meta = await get<DocumentMeta>(key);
        if (meta) metas.push(meta);
      } catch {
        // Skip corrupted entries
      }
    }
    return metas;
  } catch {
    return [];
  }
}

export async function getCachedSize(): Promise<number> {
  try {
    const metas = await getCachedDocumentMetas();
    return metas.reduce((total, m) => total + m.size, 0);
  } catch {
    return 0;
  }
}
